import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { isDrayNow } from '../../../../lib/config/multitenancy.js'
import { useTenantStore } from '../../../../stores/tenantStore.js'
import { getKindeOrgCode } from '../../lib/kindeOrg.js'
import { savePostLoginRedirect } from '../../lib/postLoginRedirect.js'

export const useKindeLoginLogic = () => {
  const kindeAuth = useKindeAuth()
  const { login, isLoading } = kindeAuth
  const autoLoginTriggered = useRef(false)
  const [isAutoLogin] = useState(
    () =>
      new URLSearchParams(window.location.search).get('autoLogin') === 'true',
  )

  // Tenant-provided URLs (populated by public config fetch on app boot)
  const tenantPrivacyUrl = useTenantStore((s) => s.privacyPolicyUrl)
  const tenantTermsUrl = useTenantStore((s) => s.termsConditionsUrl)

  // Use tenant URLs if available, otherwise fall back to brand-specific defaults
  const privacyUrl =
    tenantPrivacyUrl ||
    (isDrayNow
      ? 'https://draynow.com/privacy-policy'
      : 'https://modalview.com/privacy')
  const termsUrl =
    tenantTermsUrl ||
    (isDrayNow
      ? 'https://draynow.com/terms-of-service'
      : 'https://modalview.com/terms-of-use')

  // Computed values
  const orgCode = useMemo(() => getKindeOrgCode(), [])

  // Event handlers
  const handleKindeLogin = useCallback(async () => {
    try {
      // Pass organization code to login for branded experience
      await login({ orgCode })
    } catch (error) {
      console.error('Kinde login failed:', error)
    }
  }, [kindeAuth, login, orgCode])

  // Stash ?redirectTo= in sessionStorage at mount — BEFORE the autoLogin
  // effect below strips the query. The Kinde round-trip loses the URL query
  // entirely (Kinde only returns to the fixed /callback URI); sessionStorage
  // survives it, and KindeCallback consumes the stash after validation
  // (MV-2954, open-redirect guarded by safeRedirect).
  useEffect(() => {
    const redirectTo = new URLSearchParams(window.location.search).get(
      'redirectTo',
    )
    if (redirectTo) {
      savePostLoginRedirect(redirectTo)
    }
  }, [])

  // Auto-login for cross-tenant IMC redirect
  useEffect(() => {
    if (autoLoginTriggered.current) return

    const params = new URLSearchParams(window.location.search)
    if (params.get('autoLogin') === 'true') {
      autoLoginTriggered.current = true
      window.history.replaceState({}, '', window.location.pathname)
      handleKindeLogin()
    }
  }, [handleKindeLogin])

  return {
    // State
    isLoading,
    isAutoLogin,

    // URLs
    privacyUrl,
    termsUrl,

    // Event handlers
    handleKindeLogin,
  }
}
